/** @module components/choice-quiz */


import Model from '@lernetz/ts-lib/src/core/model/Model';
import View from '@lernetz/ts-lib/src/core/view/View';

import {Choice} from './Choice';
import {ChoiceQuizType} from './ChoiceQuiz';
import {ToggleView} from './Toggle';

import {CanShowCorrectness} from '../quiz/CanShowCorrectness';

import {CheckBoxChecked as checkBoxCheckedTemplate,
        CheckBoxUnchecked as checkBoxUncheckedTemplate,
        RadioButtonChecked as radioButtonCheckedTemplate,
        RadioButtonUnchecked as radioButtonUncheckedTemplate,
        TextChoice as template} from '../../templates/choice-quiz';


/**
 * Specifies the initial state of a [[TextChoice]].
 */
export interface TextChoiceInitialState {

    /**
     * Sets whether this choice is a (or the) correct one.
     */
    correct: boolean;

    /**
     * Sets whether this choice should be rendered as initially selected.
     *
     * Defaults to `false`.
     */
    selected?: boolean;

    /**
     * Sets whether the correctness of this choice should be initially displayed.
     * Defaults to `false`.
     */
    showCorrectness?: boolean;

    /**
     * Sets the text that should be displayed for this choice.
     */
    text: string;
}


/**
 * A textual choice in a [[ChoiceQuiz]].
 *
 * @see [[Choice]], the main interface implemented by this class.
 */
export class TextChoice extends Model implements Choice, CanShowCorrectness {

    /**
     * Initializes a new instance of the [[TextChoice]] class.
     *
     * @param initialState Specifies the initial state for the new instance.
     */
    public constructor(initialState: TextChoiceInitialState) {
        super({
            correct: false,
            selected: false,
            showCorrectness: false,
            text: '',
            ...initialState,
        });
    }

    /** @inheritdoc */
    public readonly correct: boolean;

    /** @inheritdoc */
    public selected: boolean;

    /**
     * Gets or sets whether the correctness of this choice should be shown.
     */
    public showCorrectness: boolean;

    /**
     * Gets the text that should be displayed for this choice.
     */
    public readonly text: string;
}


/**
 * Base `View` class to render a [[TextChoice]].
 * Subclass this type if you want to use a different template.
 */
export class TextChoiceView extends View<TextChoice> {

    /**
     * Initializes a new instance of the [[TextChoiceView]] class.
     */
    public constructor(type: ChoiceQuizType) {
        super({
            template,
            registrations: {
                'Toggle': () => new ToggleView(...TextChoiceView.getToggleTemplates(type)),
            },
        })
    }

    private static getToggleTemplates(type: ChoiceQuizType): [string, string] {
        return type === 'single' ? [radioButtonCheckedTemplate, radioButtonUncheckedTemplate]
                                 : [checkBoxCheckedTemplate,    checkBoxUncheckedTemplate   ];
    }
}
